import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import { ArrowLeft, FileText, MessageSquare, Shield, Clock, Send, DollarSign, AlertTriangle, CheckCircle2, Lock, Pause, Play, XCircle, StickyNote, Upload, Download, Wallet } from "lucide-react";
import { StatusPill } from "@/pages/Deals";
import AiDealAssistantPanel from "@/components/app/AiDealAssistantPanel";

const EVENT_LABELS = {
  deal_room_created: "Deal Room created", status_changed: "Status changed",
  document_shared: "Document shared", document_unshared: "Document access revoked",
  deal_note_added: "Message posted", offer_accepted: "Offer accepted",
  funds_sent: "Buyer marked funds sent", funds_received: "Admin recorded funds received",
  funds_cleared: "Admin marked funds cleared",
  transfer_document_uploaded: "Transfer document uploaded",
  transfer_document_deleted: "Transfer document deleted",
  transfer_package_complete: "Transfer package marked complete",
  buyer_acceptance_recorded: "Buyer accepted transfer",
  additional_docs_requested: "Buyer requested more documents",
  dispute_opened: "Dispute opened", dispute_resolved: "Dispute resolved",
  settlement_recorded: "Settlement recorded", settlement_corrected: "Settlement corrected",
  transaction_closed: "Transaction closed",
  internal_note_added: "Internal note added",
};

export default function DealRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({ title: `Deal Room ${id} — FinanceLocal`, noIndex: true, canonical: `https://myfinancelocal.com/deals/${id}` });

  const reload = () => {
    api.get(`/deals/${id}`).then((r) => setDeal(r.data)).catch(() => navigate("/deals"));
    api.get(`/deals/${id}/audit`).then((r) => setAudit(r.data.audit_events || []));
    api.get(`/deals/${id}/settlement/preview`).then((r) => setPreview(r.data)).catch(() => {});
  };
  useEffect(reload, [id]);  // eslint-disable-line

  const call = async (fn, msgOk = "Done") => {
    setBusy(true);
    try { await fn(); toast.success(msgOk); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!user || !deal) return <div className="mx-auto max-w-6xl px-4 py-16 text-muted-foreground" data-testid="deal-loading">Loading…</div>;

  const role = deal.your_role;
  const isBuyer = role === "buyer";
  const isSeller = role === "seller";
  const isAdmin = user.role === "admin";
  const closed = !!deal.closed;
  const n = deal.note || {};

  // If closed, jump to Closing Summary
  if (closed) return <ClosingSummary dealId={id} audit={audit} />;

  return (
    <div data-testid="page-deal-room" className="mx-auto max-w-6xl px-4 py-10">
      <Link to="/deals" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-deals">
        <ArrowLeft className="w-4 h-4" /> Back to My Deals
      </Link>

      <div className="border-b border-border pb-6 mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs text-muted-foreground" data-testid="deal-id-label">{deal.deal_id}</div>
          <h1 className="font-display text-3xl mt-1" data-testid="deal-vehicle-label">{n.vehicle_year} {n.vehicle_make} {n.vehicle_model}</h1>
          <div className="text-xs text-muted-foreground mt-1">
            You are the <b className="text-foreground" data-testid="deal-your-role">{role}</b> · Grade {n.grade} · {n.state}
          </div>
        </div>
        <div className="text-right space-y-2">
          <StatusPill status={deal.status} />
          <div className="font-display text-3xl text-primary" data-testid="deal-final-amount">{fmtMoney(deal.final_amount || 0)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <div className="space-y-8">
          {/* Buyer balance summary — total purchase / deposit received / remaining */}
          {(isBuyer || isSeller || isAdmin) && deal.deposit_summary && (
            <BalanceSummaryCard summary={deal.deposit_summary} role={role} />
          )}

          {/* Lifecycle actions (role-aware) */}
          {isBuyer && ["due_diligence", "opened"].includes(deal.status) && (
            <SectionButton testid="btn-begin-due-diligence" onClick={() => call(() => api.post(`/deals/${id}/status`, { status: "due_diligence" }), "Due diligence started")} label="Begin Due Diligence" busy={busy} hidden={deal.status !== "opened"} />
          )}

          {/* STEP 8 — Buyer funds sent */}
          {isBuyer && !deal.funds?.sent && ["due_diligence", "funding_pending"].includes(deal.status) && (
            <FundsSentForm dealId={id} onDone={reload} busy={busy} setBusy={setBusy} />
          )}
          {deal.funds?.sent && <FundsSentSummary rec={deal.funds.sent} />}

          {/* STEP 9 — Admin funds received / cleared */}
          {isAdmin && (
            <AdminFundsPanel deal={deal} onChange={reload} busy={busy} setBusy={setBusy} />
          )}

          {/* STEP 10 — Seller transfer documents */}
          {(isSeller || isAdmin) && !deal.transfer_package_complete && (
            <SellerTransferPanel deal={deal} onChange={reload} busy={busy} setBusy={setBusy} />
          )}
          {deal.transfer_docs && deal.transfer_docs.length > 0 && (
            <TransferDocList docs={(deal.transfer_docs || []).filter((d) => !d.deleted)} completed={deal.transfer_package_complete} dealId={id} />
          )}

          {/* STEP 11 — Buyer decision */}
          {isBuyer && deal.transfer_package_complete && !deal.buyer_acceptance && deal.status === "buyer_review" && (
            <BuyerDecisionPanel dealId={id} onChange={reload} busy={busy} setBusy={setBusy} />
          )}

          {/* Dispute — any participant */}
          {!deal.dispute?.status || deal.dispute.status === "resolved" ? (
            (isBuyer || isSeller) && !closed && (
              <OpenDisputeButton dealId={id} onChange={reload} busy={busy} setBusy={setBusy} />
            )
          ) : (
            <DisputePanel dispute={deal.dispute} dealId={id} isAdmin={isAdmin} onChange={reload} />
          )}

          {/* STEP 12 — Settlement (admin only) */}
          {isAdmin && ["ready_for_settlement", "buyer_review"].includes(deal.status) && (
            <AdminSettlementPanel dealId={id} preview={preview} onChange={reload} busy={busy} setBusy={setBusy} />
          )}
          {deal.settlement && <SettlementSummary settlement={deal.settlement} isAdmin={isAdmin} dealId={id} onChange={reload} />}

          {/* STEP 13 — Close */}
          {isAdmin && deal.status === "settled" && (
            <SectionButton testid="btn-close-deal" label="Close Deal" onClick={() => call(() => api.post(`/deals/${id}/close`), "Deal closed")} busy={busy} />
          )}

          {/* AI Deal Assistant — buyer/seller only, party-scoped */}
          {(isBuyer || isSeller) && (
            <AiDealAssistantPanel dealId={id} buyerId={deal.buyer_id} sellerId={deal.seller_id} />
          )}

          {/* Messages */}
          <section data-testid="deal-messages">
            <SectionHeader icon={<MessageSquare className="w-4 h-4" />}>Messages</SectionHeader>
            <div className="border border-border max-h-[300px] overflow-y-auto" data-testid="deal-messages-list">
              {(deal.messages || []).length === 0 && <div className="p-4 text-sm text-muted-foreground" data-testid="deal-no-messages">No messages yet.</div>}
              {(deal.messages || []).map((m) => (
                <div key={m.message_id} className="p-3 border-b border-border last:border-b-0" data-testid={`deal-msg-${m.message_id}`}>
                  <div className="text-xs text-muted-foreground font-mono-data mb-1">{m.from_role} · {new Date(m.at).toLocaleString()}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Textarea data-testid="deal-msg-input" className="rounded-none" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" />
              <Button data-testid="btn-send-msg" onClick={() => call(async () => { await api.post(`/deals/${id}/messages`, { text: msg }); setMsg(""); }, "Message sent")} disabled={busy || !msg.trim()} className="rounded-none uppercase text-xs tracking-wider"><Send className="w-4 h-4" /></Button>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          {/* Settlement gate visibility for all */}
          {preview && (
            <div className="border border-border p-4" data-testid="settlement-gate">
              <SectionHeader icon={<CheckCircle2 className="w-4 h-4" />}>Settlement gate</SectionHeader>
              <div className={"text-xs mb-2 " + (preview.gate_ok ? "text-primary" : "text-amber-700 dark:text-amber-300")} data-testid="gate-status">
                {preview.gate_ok ? "All conditions satisfied ✓" : "Blocked · pending items below"}
              </div>
              <ul className="text-[11px] space-y-1" data-testid="gate-conditions">
                {["buyer_acceptance","funds_received","funds_cleared","transfer_documents","transfer_package_complete","dispute_open"].map((k) => (
                  <li key={k} data-testid={`gate-${k}`} className={"flex items-center gap-2 " + (k === "dispute_open"
                    ? (preview.unmet_conditions.includes(k) ? "text-red-600" : "text-muted-foreground")
                    : (preview.unmet_conditions.includes(k) ? "text-muted-foreground" : "text-primary"))}>
                    {k === "dispute_open"
                      ? (preview.unmet_conditions.includes(k) ? "✗" : "○")
                      : (preview.unmet_conditions.includes(k) ? "○" : "✓")} {k.replaceAll("_", " ")}
                  </li>
                ))}
              </ul>
              <div className="mt-3 border-t border-border pt-3 space-y-1 text-xs">
                <Row lbl="Gross" val={fmtMoney(preview.gross_purchase_price || 0)} />
                <Row lbl="Buyer fee (2%)" val={fmtMoney(preview.fees.buyer_fee)} testid="fee-buyer" />
                <Row lbl="Seller fee (2%)" val={fmtMoney(preview.fees.seller_fee)} testid="fee-seller" />
                <Row lbl="Total platform fee" val={fmtMoney(preview.fees.combined_fee)} strong testid="fee-total" />
                <Row lbl="Net seller proceeds" val={fmtMoney(preview.net_seller_proceeds)} strong testid="fee-net" />
                {preview.fees.minimum_applied && <div className="text-[10px] text-amber-700 dark:text-amber-300" data-testid="fee-min-applied">$500 minimum applied</div>}
              </div>
            </div>
          )}

          {isAdmin && <AdminControlsPanel deal={deal} onChange={reload} busy={busy} setBusy={setBusy} />}

          <div className="border border-border p-4" data-testid="deal-audit-panel">
            <SectionHeader icon={<Clock className="w-4 h-4" />}>Audit trail</SectionHeader>
            <ol className="space-y-2 text-xs" data-testid="deal-audit-list">
              {audit.map((ev, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`deal-audit-${ev.event}-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <div className="flex-1">
                    <div className="font-mono-data">{EVENT_LABELS[ev.event] || ev.event}</div>
                    <div className="text-[10px] text-muted-foreground">{new Date(ev.at).toLocaleString()} · by {ev.actor_role}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="border border-border p-4 bg-muted/20 text-xs text-muted-foreground" data-testid="deal-disclaimer">
            <div className="flex items-center gap-2 font-overline mb-2"><Shield className="w-4 h-4" /> Admin role</div>
            FinanceLocal facilitates this transaction and maintains the audit trail. FinanceLocal is not a party to the sale and does not determine whether the buyer should proceed or whether the seller&apos;s disclosures are adequate.
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- helpers ----------
const SectionHeader = ({ icon, children }) => <div className="flex items-center gap-2 font-overline text-muted-foreground mb-3">{icon}{children}</div>;
const Row = ({ lbl, val, strong, testid }) => (
  <div className="flex justify-between" data-testid={testid}><span>{lbl}</span><span className={"font-mono-data " + (strong ? "font-semibold" : "")}>{val}</span></div>
);
const SectionButton = ({ testid, label, onClick, busy, hidden }) => hidden ? null : (
  <div><Button data-testid={testid} disabled={busy} onClick={onClick} className="rounded-none uppercase text-xs tracking-wider h-11">{label}</Button></div>
);

// ---------- STEP 8 ----------
function FundsSentForm({ dealId, onDone, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [dateSent, setDateSent] = useState("");
  const [reference, setReference] = useState("");
  const [bankName, setBankName] = useState("");
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/funds/sent`, { amount: Number(amount), date_sent: dateSent, reference, bank_name: bankName });
      toast.success("Funds recorded"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="border border-border p-4" data-testid="buyer-funds-form">
      <SectionHeader icon={<DollarSign className="w-4 h-4" />}>Send purchase funds</SectionHeader>
      <div className="text-xs text-muted-foreground mb-4">
        Wire or ACH to FinanceLocal&apos;s transaction account. Instructions are emailed on offer acceptance.
        Note-purchase funds are <b>never</b> processed through Stripe.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount sent ($)"><Input data-testid="funds-sent-amount" type="number" className="rounded-none font-mono-data" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Date sent"><Input data-testid="funds-sent-date" type="date" className="rounded-none" value={dateSent} onChange={(e) => setDateSent(e.target.value)} /></Field>
        <Field label="Bank / reference #"><Input data-testid="funds-sent-ref" className="rounded-none font-mono-data" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
        <Field label="Bank name (optional)"><Input data-testid="funds-sent-bank" className="rounded-none" value={bankName} onChange={(e) => setBankName(e.target.value)} /></Field>
      </div>
      <Button data-testid="btn-funds-sent" onClick={submit} disabled={busy || !amount || !dateSent || !reference} className="rounded-none uppercase text-xs tracking-wider mt-3">Mark funds sent</Button>
    </section>
  );
}
function FundsSentSummary({ rec }) {
  return <div className="border border-border p-4 text-sm" data-testid="funds-sent-summary">
    <SectionHeader icon={<DollarSign className="w-4 h-4" />}>Funds sent (buyer)</SectionHeader>
    <Row lbl="Amount" val={fmtMoney(rec.amount)} />
    <Row lbl="Date sent" val={rec.date_sent} />
    <Row lbl="Reference" val={rec.reference} />
    {rec.bank_name && <Row lbl="Bank" val={rec.bank_name} />}
  </div>;
}

// ---------- STEP 9 ----------
function AdminFundsPanel({ deal, onChange, busy, setBusy }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [ref, setRef] = useState("");
  const [cd, setCd] = useState("");
  const alreadyReceived = !!(deal.funds && deal.funds.received);
  const alreadyCleared = !!(deal.funds && deal.funds.cleared);
  const recordReceived = async () => {
    setBusy(true);
    try { await api.post(`/deals/${deal.deal_id}/funds/received`, { amount: Number(amount), date_received: date, reference: ref }); onChange(); toast.success("Funds received recorded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const markCleared = async () => {
    setBusy(true);
    try { await api.post(`/deals/${deal.deal_id}/funds/cleared`, { cleared_date: cd }); onChange(); toast.success("Funds cleared"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <section className="border border-border p-4" data-testid="admin-funds-panel">
      <SectionHeader icon={<DollarSign className="w-4 h-4" />}>Admin funds ledger</SectionHeader>
      {alreadyReceived ? <div className="text-xs text-primary mb-2" data-testid="funds-received-badge">✓ Received {fmtMoney(deal.funds.received.amount)} on {deal.funds.received.date_received}</div>
        : <div className="grid grid-cols-3 gap-2 mb-3">
          <Input data-testid="funds-received-amount" placeholder="Amount" type="number" className="rounded-none font-mono-data" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Input data-testid="funds-received-date" type="date" className="rounded-none" value={date} onChange={(e) => setDate(e.target.value)} />
          <Input data-testid="funds-received-ref" placeholder="Ref #" className="rounded-none font-mono-data" value={ref} onChange={(e) => setRef(e.target.value)} />
          <Button data-testid="btn-funds-received" onClick={recordReceived} disabled={busy || !amount || !date || !ref} className="rounded-none uppercase text-xs tracking-wider col-span-3">Record received</Button>
        </div>
      }
      {alreadyCleared ? <div className="text-xs text-primary" data-testid="funds-cleared-badge">✓ Cleared on {deal.funds.cleared.cleared_date}</div>
        : alreadyReceived && (
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="funds-cleared-date" type="date" className="rounded-none col-span-2" value={cd} onChange={(e) => setCd(e.target.value)} />
            <Button data-testid="btn-funds-cleared" onClick={markCleared} disabled={busy || !cd} className="rounded-none uppercase text-xs tracking-wider">Mark cleared</Button>
          </div>
        )}
    </section>
  );
}

// ---------- Balance summary (buyer + parties) ----------
const DOC_TYPE_LABELS = {
  title: "Title",
  assignment: "Assignment",
  payment_history: "Payment History",
  insurance: "Insurance",
  other: "Other",
};

function BalanceSummaryCard({ summary, role }) {
  const s = summary || {};
  const purchase = s.total_purchase || 0;
  const held = s.deposit_held || 0;
  const released = s.deposit_released || 0;
  const credited = s.credited_total || 0;
  const remaining = s.remaining_balance || 0;
  const paid = purchase > 0 ? Math.min(credited / purchase, 1) : 0;
  return (
    <section className="border border-primary/40 bg-primary/5 p-5" data-testid="deal-balance-summary">
      <div className="flex items-center gap-2 font-overline text-primary mb-3"><Wallet className="w-4 h-4" /> Purchase balance</div>
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div data-testid="balance-total-purchase">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total purchase</div>
          <div className="font-display text-2xl">{fmtMoney(purchase)}</div>
        </div>
        <div data-testid="balance-deposits-credited">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deposits received</div>
          <div className="font-display text-2xl text-primary">{fmtMoney(credited)}</div>
          {(held > 0 || released > 0) && (
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {held > 0 && <span data-testid="balance-held">{fmtMoney(held)} held</span>}
              {held > 0 && released > 0 && " · "}
              {released > 0 && <span data-testid="balance-released">{fmtMoney(released)} released</span>}
            </div>
          )}
        </div>
        <div data-testid="balance-remaining">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {role === "buyer" ? "You still owe" : "Buyer still owes"}
          </div>
          <div className={"font-display text-2xl " + (remaining > 0 ? "text-amber-700 dark:text-amber-300" : "text-primary")} data-testid="balance-remaining-value">
            {fmtMoney(remaining)}
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-muted overflow-hidden" data-testid="balance-progress">
        <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(paid * 100)}%` }} />
      </div>
      {role === "buyer" && remaining > 0 && (
        <div className="text-xs text-muted-foreground mt-3" data-testid="balance-buyer-instructions">
          Wire the remaining <b>{fmtMoney(remaining)}</b> to FinanceLocal&apos;s transaction account to complete this purchase. Wire instructions are emailed on offer acceptance.
        </div>
      )}
      {remaining === 0 && purchase > 0 && (
        <div className="text-xs text-primary mt-3" data-testid="balance-fully-funded">✓ Purchase fully funded.</div>
      )}
    </section>
  );
}

// ---------- STEP 10 ----------
function SellerTransferPanel({ deal, onChange, busy, setBusy }) {
  const [dtype, setDtype] = useState("title");
  const [desc, setDesc] = useState("");
  const [selFile, setSelFile] = useState(null);
  const active = (deal.transfer_docs || []).filter((d) => !d.deleted);
  const upload = async () => {
    if (!selFile) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", selFile);
      form.append("doc_type", dtype);
      if (desc.trim()) form.append("description", desc.trim());
      await api.post(`/deals/${deal.deal_id}/transfer-docs/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelFile(null); setDesc("");
      onChange(); toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (docId) => {
    setBusy(true);
    try { await api.delete(`/deals/${deal.deal_id}/transfer-docs/${docId}`); onChange(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const markComplete = async () => {
    setBusy(true);
    try { await api.post(`/deals/${deal.deal_id}/transfer-package/mark-complete`); onChange(); toast.success("Transfer package marked complete"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <section className="border border-border p-4" data-testid="seller-transfer-panel">
      <SectionHeader icon={<FileText className="w-4 h-4" />}>Transfer documents (seller)</SectionHeader>
      <div className="text-xs text-muted-foreground mb-3">
        Upload the ownership-transfer documents so the buyer can accept and complete the deal. PDF, JPG, or PNG · max 25 MB per file.
      </div>
      <div className="grid grid-cols-1 gap-2 mb-2">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Document type">
            <select data-testid="transfer-doc-type" value={dtype} onChange={(e) => setDtype(e.target.value)} className="border border-border rounded-none px-2 py-2 text-sm bg-background w-full">
              {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="File">
            <Input
              data-testid="transfer-doc-file"
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              className="rounded-none file:mr-2 file:rounded-none file:border-0 file:bg-primary file:text-primary-foreground file:px-2 file:py-1 file:text-xs"
              onChange={(e) => setSelFile(e.target.files?.[0] || null)}
            />
          </Field>
        </div>
        <Field label="Description (optional)">
          <Textarea data-testid="transfer-doc-desc" placeholder="Short description" rows={2} className="rounded-none" value={desc} onChange={(e) => setDesc(e.target.value)} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button data-testid="btn-upload-transfer" onClick={upload} disabled={busy || !selFile} className="rounded-none uppercase text-xs tracking-wider">
          <Upload className="w-3 h-3 mr-1" /> Upload
        </Button>
        <Button data-testid="btn-mark-transfer-complete" onClick={markComplete} disabled={busy || active.length === 0} variant="outline" className="rounded-none uppercase text-xs tracking-wider">
          Mark package complete
        </Button>
      </div>
      {active.length > 0 && (
        <ul className="border border-border divide-y divide-border mt-3" data-testid="transfer-doc-list-editable">
          {active.map((d) => (
            <li key={d.doc_id} className="p-2 flex items-center justify-between text-xs" data-testid={`transfer-doc-${d.doc_id}`}>
              <div className="min-w-0 flex-1">
                <div><b>{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</b> <span className="text-muted-foreground truncate">— {d.doc_ref}</span></div>
                {d.description && <div className="text-[10px] text-muted-foreground mt-0.5">{d.description}</div>}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <DownloadDocBtn dealId={deal.deal_id} docId={d.doc_id} filename={d.doc_ref} />
                <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => remove(d.doc_id)} data-testid={`btn-del-tdoc-${d.doc_id}`}>Delete</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
function DownloadDocBtn({ dealId, docId, filename }) {
  const dl = async () => {
    try {
      const r = await api.get(`/deals/${dealId}/transfer-docs/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "document"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("Download failed"); }
  };
  return <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={dl} data-testid={`btn-dl-tdoc-${docId}`}><Download className="w-3 h-3" /></Button>;
}
function TransferDocList({ docs, completed, dealId }) {
  return <div className="border border-border p-4" data-testid="transfer-doc-list">
    <SectionHeader icon={<FileText className="w-4 h-4" />}>Transfer package {completed && <span className="text-primary">· complete ✓</span>}</SectionHeader>
    <ul className="text-sm space-y-1">
      {docs.map((d) => (
        <li key={d.doc_id} className="flex justify-between items-center" data-testid={`viewer-tdoc-${d.doc_id}`}>
          <span><b>{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</b> — {d.doc_ref}</span>
          <span className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString()}</span>
            {d.storage_path === undefined && d.doc_ref && <DownloadDocBtn dealId={dealId} docId={d.doc_id} filename={d.doc_ref} />}
          </span>
        </li>
      ))}
    </ul>
  </div>;
}

// ---------- STEP 11 ----------
function BuyerDecisionPanel({ dealId, onChange, busy, setBusy }) {
  const [conf, setConf] = useState("");
  const [reqText, setReqText] = useState("");
  const accept = async () => {
    setBusy(true);
    try { await api.post(`/deals/${dealId}/buyer/accept`, { confirmation: conf }); onChange(); toast.success("Transfer accepted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const requestMore = async () => {
    setBusy(true);
    try { await api.post(`/deals/${dealId}/buyer/request-more`, { request_text: reqText }); onChange(); setReqText(""); toast.success("Request sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <section className="border border-border p-4" data-testid="buyer-decision-panel">
      <SectionHeader icon={<CheckCircle2 className="w-4 h-4" />}>Buyer decision</SectionHeader>
      <div className="text-xs text-muted-foreground mb-3">Review the transfer package. Type your full name to confirm acceptance, or request additional documents.</div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <Input data-testid="buyer-accept-confirmation" placeholder="Type your full name to accept" className="rounded-none" value={conf} onChange={(e) => setConf(e.target.value)} />
        <Button data-testid="btn-buyer-accept" onClick={accept} disabled={busy || !conf.trim()} className="rounded-none uppercase text-xs tracking-wider">Accept transfer</Button>
      </div>
      <Textarea data-testid="buyer-more-request" placeholder="What additional document(s) do you need?" rows={2} className="rounded-none" value={reqText} onChange={(e) => setReqText(e.target.value)} />
      <Button data-testid="btn-buyer-request-more" onClick={requestMore} disabled={busy || !reqText.trim()} variant="outline" className="rounded-none uppercase text-xs tracking-wider mt-2">Request more docs</Button>
    </section>
  );
}
function OpenDisputeButton({ dealId, onChange, busy, setBusy }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/deals/${dealId}/dispute`, { reason, description: desc }); setOpen(false); onChange(); toast.success("Dispute opened"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  if (!open) return <Button data-testid="btn-open-dispute" variant="outline" className="rounded-none uppercase text-xs tracking-wider text-red-600 border-red-300" onClick={() => setOpen(true)}>Open dispute</Button>;
  return (
    <section className="border border-red-300 dark:border-red-900 p-4" data-testid="dispute-form">
      <SectionHeader icon={<AlertTriangle className="w-4 h-4" />}>Open a dispute</SectionHeader>
      <Input data-testid="dispute-reason" placeholder="Short reason" className="rounded-none mb-2" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Textarea data-testid="dispute-description" placeholder="Describe the issue in detail" rows={3} className="rounded-none mb-2" value={desc} onChange={(e) => setDesc(e.target.value)} />
      <div className="flex gap-2">
        <Button data-testid="btn-submit-dispute" onClick={submit} disabled={busy || !reason.trim() || !desc.trim()} className="rounded-none uppercase text-xs tracking-wider">Submit dispute</Button>
        <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </section>
  );
}
function DisputePanel({ dispute, dealId, isAdmin, onChange }) {
  const [resolution, setResolution] = useState("");
  const resolve = async () => {
    try { await api.post(`/deals/${dealId}/dispute/resolve`, { resolution }); onChange(); toast.success("Dispute resolved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <section className="border border-red-300 dark:border-red-900 p-4" data-testid="dispute-panel-open">
      <SectionHeader icon={<AlertTriangle className="w-4 h-4 text-red-600" />}>Dispute · OPEN</SectionHeader>
      <div className="text-sm mb-2"><b>{dispute.reason}</b></div>
      <div className="text-xs text-muted-foreground mb-3">{dispute.description}</div>
      {isAdmin && (
        <>
          <Input data-testid="dispute-resolution" placeholder="Resolution note" className="rounded-none mb-2" value={resolution} onChange={(e) => setResolution(e.target.value)} />
          <Button data-testid="btn-resolve-dispute" onClick={resolve} className="rounded-none uppercase text-xs tracking-wider">Resolve dispute</Button>
        </>
      )}
    </section>
  );
}

// ---------- STEP 12 ----------
function AdminSettlementPanel({ dealId, preview, onChange, busy, setBusy }) {
  const [releaseDate, setRD] = useState("");
  const [amt, setAmt] = useState("");
  const [meth, setMeth] = useState("wire");
  const [ref, setRef] = useState("");
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/deals/${dealId}/settlement`, { release_date: releaseDate, amount_released: Number(amt), payment_method: meth, reference: ref }); onChange(); toast.success("Settlement recorded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <section className="border border-border p-4" data-testid="admin-settlement-panel">
      <SectionHeader icon={<Lock className="w-4 h-4" />}>Record settlement</SectionHeader>
      {preview && !preview.gate_ok && <div className="text-xs text-amber-700 dark:text-amber-300 mb-2" data-testid="settlement-blocked">Gate not satisfied: {preview.unmet_conditions.join(", ")}</div>}
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="settle-date" type="date" className="rounded-none" value={releaseDate} onChange={(e) => setRD(e.target.value)} />
        <Input data-testid="settle-amount" type="number" placeholder="Amount released" className="rounded-none font-mono-data" value={amt} onChange={(e) => setAmt(e.target.value)} />
        <select data-testid="settle-method" value={meth} onChange={(e) => setMeth(e.target.value)} className="border border-border rounded-none px-2 py-1 text-sm bg-background">
          <option value="wire">Wire</option><option value="ach">ACH</option><option value="check">Check</option><option value="other">Other</option>
        </select>
        <Input data-testid="settle-ref" placeholder="Reference #" className="rounded-none font-mono-data" value={ref} onChange={(e) => setRef(e.target.value)} />
      </div>
      <Button data-testid="btn-record-settlement" onClick={submit} disabled={busy || !preview?.gate_ok || !releaseDate || !amt || !ref} className="rounded-none uppercase text-xs tracking-wider mt-3">Record & lock settlement</Button>
    </section>
  );
}
function SettlementSummary({ settlement, isAdmin, dealId, onChange }) {
  const [showCor, setShowCor] = useState(false);
  const [reason, setReason] = useState("");
  const [field, setField] = useState("amount_released");
  const [val, setVal] = useState("");
  const correct = async () => {
    try { await api.post(`/deals/${dealId}/settlement/correct`, { reason, changes: { [field]: field === "amount_released" ? Number(val) : val } }); onChange(); setShowCor(false); setReason(""); setVal(""); toast.success("Correction logged"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <div className="border border-border p-4" data-testid="settlement-summary">
    <SectionHeader icon={<Lock className="w-4 h-4" />}>Settlement · locked</SectionHeader>
    <Row lbl="Amount released" val={fmtMoney(settlement.amount_released)} testid="settle-summary-amount" />
    <Row lbl="Date" val={settlement.release_date} />
    <Row lbl="Method" val={settlement.payment_method} />
    <Row lbl="Reference" val={settlement.reference} />
    {(settlement.corrections || []).length > 0 && (
      <div className="mt-2 border-t border-border pt-2 text-xs" data-testid="settle-corrections">
        <b>{settlement.corrections.length} correction(s)</b>
        {settlement.corrections.map((c) => <div key={c.correction_id} data-testid={`correction-${c.correction_id}`} className="text-muted-foreground">· {c.reason} ({Object.keys(c.changes).join(", ")})</div>)}
      </div>
    )}
    {isAdmin && !showCor && <Button data-testid="btn-open-correction" variant="outline" size="sm" className="rounded-none uppercase text-xs tracking-wider mt-2" onClick={() => setShowCor(true)}>Log correction</Button>}
    {isAdmin && showCor && (
      <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid="correction-form">
        <Input data-testid="correction-reason" placeholder="Reason" className="rounded-none" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex gap-2">
          <select data-testid="correction-field" value={field} onChange={(e) => setField(e.target.value)} className="border border-border rounded-none px-2 py-1 text-sm bg-background">
            <option value="amount_released">amount_released</option><option value="release_date">release_date</option>
            <option value="payment_method">payment_method</option><option value="reference">reference</option>
            <option value="internal_note">internal_note</option>
          </select>
          <Input data-testid="correction-value" placeholder="New value" className="rounded-none flex-1" value={val} onChange={(e) => setVal(e.target.value)} />
          <Button data-testid="btn-log-correction" onClick={correct} disabled={!reason.trim() || !val.trim()} className="rounded-none uppercase text-xs tracking-wider">Submit</Button>
        </div>
      </div>
    )}
  </div>;
}

// ---------- Admin controls ----------
function AdminControlsPanel({ deal, onChange, busy, setBusy }) {
  const [note, setNote] = useState("");
  const [cancelReason, setCR] = useState("");
  const act = async (fn) => { setBusy(true); try { await fn(); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  return <div className="border border-border p-4 space-y-3" data-testid="admin-controls">
    <SectionHeader icon={<StickyNote className="w-4 h-4" />}>Admin controls</SectionHeader>
    <div className="flex gap-2 flex-wrap">
      {deal.status !== "paused" && <Button data-testid="btn-admin-pause" size="sm" variant="outline" className="rounded-none text-xs" onClick={() => act(() => api.post(`/deals/${deal.deal_id}/pause`))} disabled={busy}><Pause className="w-3 h-3 mr-1" />Pause</Button>}
      {deal.status === "paused" && <Button data-testid="btn-admin-resume" size="sm" variant="outline" className="rounded-none text-xs" onClick={() => act(() => api.post(`/deals/${deal.deal_id}/resume`))} disabled={busy}><Play className="w-3 h-3 mr-1" />Resume</Button>}
      <Button data-testid="btn-admin-cancel" size="sm" variant="outline" className="rounded-none text-xs text-red-600 border-red-300" onClick={() => { const r = window.prompt("Cancel reason:", ""); if (r) act(() => api.post(`/deals/${deal.deal_id}/cancel`, { reason: r })); }} disabled={busy}><XCircle className="w-3 h-3 mr-1" />Cancel</Button>
    </div>
    <div className="flex gap-2">
      <Input data-testid="admin-internal-note" placeholder="Internal note (admins only)" className="rounded-none" value={note} onChange={(e) => setNote(e.target.value)} />
      <Button data-testid="btn-internal-note" onClick={() => act(async () => { await api.post(`/deals/${deal.deal_id}/internal-note`, { text: note }); setNote(""); })} disabled={busy || !note.trim()} className="rounded-none uppercase text-xs tracking-wider">Add</Button>
    </div>
    {(deal.internal_notes || []).length > 0 && <ul className="text-xs space-y-1 mt-2" data-testid="admin-internal-notes-list">
      {(deal.internal_notes || []).map((n) => <li key={n.note_id} className="border-l-2 border-primary pl-2" data-testid={`internal-note-${n.note_id}`}>{n.text}</li>)}
    </ul>}
  </div>;
}

// ---------- Closing Summary ----------
function ClosingSummary({ dealId, audit }) {
  const [sum, setSum] = useState(null);
  useEffect(() => { api.get(`/deals/${dealId}/closing-summary`).then((r) => setSum(r.data)); }, [dealId]);
  useSEO({ title: `Closing Summary ${dealId} — FinanceLocal`, noIndex: true });
  if (!sum) return <div className="mx-auto max-w-6xl px-4 py-16 text-muted-foreground" data-testid="closing-loading">Loading closing summary…</div>;
  const fees = sum.fee_breakdown || {};
  return (
    <div data-testid="page-closing-summary" className="mx-auto max-w-4xl px-4 py-10">
      <Link to="/deals" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6"><ArrowLeft className="w-4 h-4" /> Back to My Deals</Link>
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Deal Closed · Read-only</div>
        <h1 className="font-display text-4xl mt-2">Closing Summary</h1>
        <p className="text-xs text-muted-foreground mt-2">Deal {sum.deal_id} · closed {sum.closed_at && new Date(sum.closed_at).toLocaleString()}</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Card title="Deal details">
          <Row lbl="Vehicle" val={`${sum.note?.vehicle_year} ${sum.note?.vehicle_make} ${sum.note?.vehicle_model}`} />
          <Row lbl="Grade" val={sum.note?.grade} />
          <Row lbl="State" val={sum.note?.state} />
        </Card>
        <Card title="Parties">
          <Row lbl="Buyer" val={sum.buyer?.email} />
          <Row lbl="Seller" val={sum.seller?.email || "platform"} />
        </Card>
        <Card title="Purchase price & fees">
          <Row lbl="Gross purchase price" val={fmtMoney(sum.gross_purchase_price)} testid="closing-gross" />
          <Row lbl="Buyer fee" val={fmtMoney(fees.buyer_fee || 0)} testid="closing-buyer-fee" />
          <Row lbl="Seller fee" val={fmtMoney(fees.seller_fee || 0)} testid="closing-seller-fee" />
          <Row lbl="Total platform fee" val={fmtMoney(fees.combined_fee || 0)} strong testid="closing-total-fee" />
          <Row lbl="Net seller proceeds" val={fmtMoney(sum.net_seller_proceeds || 0)} strong testid="closing-net" />
        </Card>
        <Card title="Funds">
          <Row lbl="Sent by buyer" val={sum.funds?.sent ? fmtMoney(sum.funds.sent.amount) : "—"} />
          <Row lbl="Received" val={sum.funds?.received ? fmtMoney(sum.funds.received.amount) : "—"} />
          <Row lbl="Cleared on" val={sum.funds?.cleared?.cleared_date || "—"} />
        </Card>
        <Card title="Buyer acceptance">
          <Row lbl="Accepted at" val={sum.buyer_acceptance?.accepted_at ? new Date(sum.buyer_acceptance.accepted_at).toLocaleString() : "—"} />
          <Row lbl="Confirmation" val={sum.buyer_acceptance?.confirmation || "—"} />
        </Card>
        <Card title="Settlement">
          <Row lbl="Amount released" val={fmtMoney(sum.settlement?.amount_released || 0)} />
          <Row lbl="Method" val={sum.settlement?.payment_method || "—"} />
          <Row lbl="Reference" val={sum.settlement?.reference || "—"} />
          <Row lbl="Date" val={sum.settlement?.release_date || "—"} />
        </Card>
      </div>
      <div className="mt-8">
        <div className="font-overline text-muted-foreground mb-2">Final document list</div>
        <ul className="border border-border divide-y divide-border" data-testid="closing-doc-list">
          {(sum.transfer_docs || []).map((d) => <li key={d.doc_id} className="p-2 text-sm flex justify-between"><span><b>{d.doc_type}</b> — {d.doc_ref}</span><span className="text-xs text-muted-foreground">{new Date(d.uploaded_at).toLocaleDateString()}</span></li>)}
          {(sum.transfer_docs || []).length === 0 && <li className="p-3 text-sm text-muted-foreground">None recorded.</li>}
        </ul>
      </div>
      <div className="mt-8">
        <div className="font-overline text-muted-foreground mb-2">Transaction timeline / audit</div>
        <ol className="border border-border p-3 space-y-2 text-xs" data-testid="closing-audit-list">
          {(sum.audit_events || audit || []).map((ev, i) => (
            <li key={i} className="flex gap-3" data-testid={`closing-audit-${ev.event}-${i}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
              <div><div className="font-mono-data">{EVENT_LABELS[ev.event] || ev.event}</div><div className="text-[10px] text-muted-foreground">{new Date(ev.at).toLocaleString()} · by {ev.actor_role}</div></div>
            </li>
          ))}
        </ol>
      </div>
      <div className="mt-8 text-xs text-muted-foreground border-t border-border pt-4" data-testid="closing-locked-notice">
        This Deal Room is now read-only. The audit trail is locked. Corrections to the settlement record require a separate correction action and are also logged.
      </div>
    </div>
  );
}
const Card = ({ title, children }) => <div className="border border-border p-4"><div className="font-overline text-muted-foreground mb-2 text-xs">{title}</div>{children}</div>;
const Field = ({ label, children }) => <div><Label className="font-overline text-xs mb-1 block">{label}</Label>{children}</div>;
